import { InjectConfig, PcConfigKey } from '@ddt-web/config-layer';
import { BaseHolding, PortfolioChild, PortfolioConstants, Security, TreeNodeMap, WebUserDataConstants } from '@ddt-web/domain';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import { PortfolioIdUtil } from '@ddt-web/portfolio-data';
import { PartnerConstants } from '@ddt-web/user-data';
import * as cloneDeep from 'lodash/cloneDeep';
import * as get from 'lodash/get';

import { PortfolioChildType } from '../constants/portfolio-child-type.enum';
import { ContributingPortfolio, HoldingTableDetails } from '../models/holding-table-details.interface';
import { BlendPortfolioUtil } from './blend-portfolio.util';

export class PortfolioFlatteningUtil {
	private static readonly CASH_SECURITY = {
		identifier: 'CASH-USD@XUSD00000@',
		identifierType: WebUserDataConstants.IDENTIFIER_TYPE.SECURITY_ID,
		ticker: 'CASH-USD',
		morningstarSecId: null,
		globalId: null,
		aladdinId: null,
		cusip: 'XUSD00000',
		bcusip: 'XUSD00000',
		isin: 'Cash_USD',
		description: 'CASH',
		assetClass: 'Cash',
		assetType: 'ASSET_TYPE_CASH',
		mstarCategory: 'CASH',
		weight: null,
		value: null,
		contributingPortfolios: [],
		error: null
	};

	private static readonly MODEL_TYPES = [PortfolioChildType.MODEL_PORTFOLIO, PortfolioChildType.THIRD_PARTY];
	private static readonly MODEL_IDENTIFIER_TYPES = [WebUserDataConstants.IDENTIFIER_TYPE.MODEL_ID, WebUserDataConstants.IDENTIFIER_TYPE.THIRD_PARTY_ID];
	@InjectConfig(PcConfigKey.ENABLE_BLENDING) private static readonly enableBlending: boolean = false;

	public static transformNodeMapToHoldings(
		treeNodeMap: TreeNodeMap,
		parentPortfolio: PortfolioChild,
		parentPercentage: number,
		holdingsArray: HoldingTableDetails[],
		flattenModels: boolean = true,
		childrenPath: string = 'childrenRef',
		nodesVisited = [],
		parentValue: number = 0,
		flattenSleeve: boolean = true
	): void {

		if (parentPortfolio && !nodesVisited.includes(parentPortfolio.id)) {
			const children = get(parentPortfolio, childrenPath, []);

			children.forEach(child => {
				const holdingPercentage = child.weight ? child.weight * parentPercentage / 100 : 0;
				const holdingValue = parentValue ? child.weight * parentValue / 100 : get(child, 'value', 0); // default value as 0 otherwise any WTP call will fail
				const cashSecurity = {
					...cloneDeep(this.CASH_SECURITY),
					weight: holdingPercentage
				};

				// PARTNER securities use-case
				if (get(child, 'positionType') === PortfolioConstants.POSITION_TYPE.INVALID) {
					PortfolioFlatteningUtil.handlePartnerInvalidSecurities(child, holdingsArray, cashSecurity);

				} else {
					// check if the child needs to be further flattened
					const storedPortfolio = treeNodeMap[child.identifier] || get(treeNodeMap, `entities[${child.identifier}]`, null);
					const shouldFlattenModel = flattenModels && BlendPortfolioUtil.isModel(child);
					const isSleeve = BlendPortfolioUtil.isAPMSleeve(child);
					const shouldFlattenSleeve = flattenSleeve && isSleeve;
					if (
						shouldFlattenModel || shouldFlattenSleeve ||
						BlendPortfolioUtil.isUserPortfolio(child) ||
						(BlendPortfolioUtil.isUserPortfolio(storedPortfolio) && !isSleeve) // Fix for save portfolio workflow
					) {

						if (storedPortfolio && !storedPortfolio.errorCode) {
							// Call method again until we reach securities and there are no more child portfolios
							PortfolioFlatteningUtil.transformNodeMapToHoldings(
								treeNodeMap,
								storedPortfolio,
								holdingPercentage,
								holdingsArray,
								flattenModels,
								childrenPath,
								[...nodesVisited, parentPortfolio.id],
								holdingValue,
								flattenSleeve
							);
						} else {
							// Store as cash if the portfolio doesn't exist in our store
							holdingsArray.push(cashSecurity);
						}

					} else {
						const contributingPortfolio = parentPortfolio.description ? {
							name: parentPortfolio.description,
							weight: holdingPercentage,
							value: holdingValue
						} : null;

						// check if the holding has already been added to the array (from a different parent)
						let foundHolding = holdingsArray.find(holding => holding.identifier === child.identifier);

						if (this.enableBlending && !foundHolding) {
							const comparisonField = this.getComparisonField(child);
							foundHolding = holdingsArray.find(holding => holding[`${comparisonField}`] === child[`${comparisonField}`]);
						}

						// update existing entry for holding
						if (foundHolding) {
							PortfolioFlatteningUtil.updateFoundHolding(foundHolding, holdingPercentage, holdingValue, contributingPortfolio);
						} else {
							if (
								!flattenModels &&
								(
									PortfolioFlatteningUtil.MODEL_TYPES.includes(child.type) ||
									(PortfolioFlatteningUtil.MODEL_IDENTIFIER_TYPES.includes(child.identifierType) && PortfolioIdUtil.isModel(child.identifier))
								)
							) {
								// Store BLK Model or TPM model
								PortfolioFlatteningUtil.addModelToHoldingsArray(child, holdingPercentage, holdingValue, holdingsArray, contributingPortfolio);

							} else {
								// Create new entry for the holding (which is a Security)
								PortfolioFlatteningUtil.addNewSecurityToHoldingsArray(child, holdingPercentage, holdingValue, holdingsArray, contributingPortfolio);
							}
						}
					}
				}
			});
		}
	}

	public static getAllUniqueChildPortfolioIds(treeNodeMap: TreeNodeMap, parentPortfolio: PortfolioChild, childrenPath: string, includeModels: boolean = true): Array<string> {
		const children = parentPortfolio?.[childrenPath] ?? [];
		const ids: Array<string> = Array.from(children.reduce((acc: Set<string>, child) => {
			const identifier: string = child.identifier;
			if (BlendPortfolioUtil.isUserPortfolio(child)) {
				acc.add(identifier);
				this.getAllUniqueChildPortfolioIds(
					treeNodeMap,
					treeNodeMap[identifier] ?? treeNodeMap.entities[identifier],
					childrenPath,
					includeModels
				).forEach(acc.add, acc);
			} else if (includeModels && PortfolioIdUtil.isModel(identifier)) {
				// models are are not blended, so there is no need to recurse
				acc.add(identifier);
			}

			return acc;
		}, new Set<string>()));

		return ids;
	}

	/**
	 * Used for partner portfolios with invalid securities.

	 * - Replace invalid securities with CASH (non asset class securities)
	 *   This is not done for asset class securities because the total will exceed 100% since SUBSTITUTED securities are also added.
	 * - Setting assetClass for INVALID securities to 'Other' to show chart allocation in PE
	 * 
	 * Note: as of now, we do not persist contributing portfolios as we can only analyse one partner portfolio at a time.
	 * Once we enable multi-portfolios view, we need to add contributing portfolios here too.
	 * 
	 * @param child 
	 * @param holdingPercentage 
	 * @param securityToReplaceInvalidChild 
	 */
	private static handlePartnerInvalidSecurities(
		child: BaseHolding,
		holdingsArray: HoldingTableDetails[],
		securityToReplaceInvalidChild: HoldingTableDetails
	): void {
		if (get(child, 'allData.originPlatform.type') !== PartnerConstants.ORIGIN_PLATFORM_TYPE.ASSET_CLASS) {
			const foundHolding = holdingsArray.find(holding => holding.identifier === securityToReplaceInvalidChild.identifier);

			if (foundHolding) {
				PortfolioFlatteningUtil.updateFoundHolding(foundHolding, securityToReplaceInvalidChild.weight, securityToReplaceInvalidChild.value);

			} else {
				holdingsArray.push(Object.assign(securityToReplaceInvalidChild, { assetClass: 'Other' }));
			}
		}
	}

	private static updateFoundHolding(
		foundHolding: HoldingTableDetails,
		holdingPercentage: number,
		holdingValue: number,
		contributingPortfolio?: ContributingPortfolio
	): void {
		foundHolding.weight += holdingPercentage;
		if (foundHolding.value && foundHolding.value > 0) {
			foundHolding.value += holdingValue;
		}
		if (contributingPortfolio) {
			// NOTE: when updating tools to analyse multiple partner portfolios, update the check based on a different field
			// different partner portfolios can have the same name
			const portfolio = foundHolding.contributingPortfolios.find(portfolio => portfolio.name === contributingPortfolio.name);
			if (portfolio) {
				portfolio.value += contributingPortfolio.value;
				portfolio.weight += contributingPortfolio.weight;
			} else {
				foundHolding.contributingPortfolios.push(contributingPortfolio);
			}
		}
	}

	private static addModelToHoldingsArray(
		child: Security,
		holdingPercentage: number,
		holdingValue: number,
		holdingsArray: HoldingTableDetails[],
		contributingPortfolio?: ContributingPortfolio
	): void {
		const model = child as Security;
		holdingsArray.push({
			identifier: model.identifier,
			identifierType: model.identifierType,
			aladdinPortfolioCode: model.aladdinPortfolioCode, // for blk models
			tpmId: model.tpmId, // for third party models
			ticker: null,
			morningstarSecId: null,
			globalId: null,
			aladdinId: null,
			cusip: null,
			isin: null,
			description: model.description,
			assetClass: null,
			assetType: null,
			mstarCategory: null,
			weight: holdingPercentage,
			value: holdingValue,
			contributingPortfolios: contributingPortfolio ? [contributingPortfolio] : [],
			dateLastModified: model.dateLastModified,

			// Partner attributes
			positionType: model.positionType,
			otherIdentifiers: model.otherIdentifiers,
			originPlatform: model.originPlatform,
			substitutedFrom: model.substitutedFrom,

			// error handling
			error: null // not applied to models
		});
	}

	private static addNewSecurityToHoldingsArray(
		child: Security,
		holdingPercentage: number,
		holdingValue: number,
		holdingsArray: HoldingTableDetails[],
		contributingPortfolio?: ContributingPortfolio
	): void {
		const security = child as Security;
		const { identifier, identifierType } = HoldingsUtil.generateIdentifierAndIdentifierType(security);
		holdingsArray.push({
			...security,
			...HoldingsUtil.retrieveImportantSecurityIdentifiers(security),
			identifier,
			identifierType,
			mstarCategory: security.mstarCategory || security.morningstarCategory,
			weight: holdingPercentage,
			value: holdingValue,
			contributingPortfolios: contributingPortfolio ? [contributingPortfolio] : []
		} as HoldingTableDetails);
	}

	private static getComparisonField(child): string {
		if (child.globalId) {
			return 'globalId';
		} else if (child.aladdinId) {
			return 'aladdinId';
		} else {
			return 'isin';
		}
	}
}
